import type { ApiNodeDataCustomData } from '@onefootprint/request-types/dashboard';
import type { CustomQuestionsFormData } from '../types';

const convertToFormData = (page: ApiNodeDataCustomData['page']): Partial<CustomQuestionsFormData> => {
  const generateId = () => Math.random().toString(36).substring(2) + Date.now().toString(36);

  return {
    title: page.header || '',
    subtitle: page.subheader || '',
    choice: {
      saveValue: !!page.choice?.di,
      dataIdentifier: page.choice?.di ?? '',
      buttons: (page.choice?.buttons ?? []).map(button => ({
        id: generateId(),
        label: button.label,
        // The drawer authors text only, so saving turns a number or bool set through the API into text.
        value: button.value === undefined || button.value === null ? '' : String(button.value),
        style: button.style,
      })),
    },
    questions: page.sections.map(section => {
      const id = generateId();

      if (section.kind === 'text_block') {
        return {
          id,
          kind: 'text_block' as const,
          content: section.content,
          condition: section.condition,
        };
      }

      const baseQuestion = {
        id,
        label: section.prompt,
        // `di` is already the full DataIdentifier the form model stores (`custom.*` or `id.*`).
        dataIdentifier: section.di,
        required: section.required,
        condition: section.condition,
      };

      if (section.config.kind === 'short_text') {
        const cfg = section.config;
        if (cfg.format === 'any') {
          return {
            ...baseQuestion,
            kind: 'short_text' as const,
            format: 'any' as const,
            placeholder: cfg.placeholder,
            regex: cfg.regex ?? undefined,
            minLength: cfg.minLength ?? undefined,
            maxLength: cfg.maxLength ?? undefined,
            errorMessage: cfg.errorMessage ?? undefined,
          };
        }
        if (cfg.format === 'dob') {
          return {
            ...baseQuestion,
            kind: 'short_text' as const,
            format: 'dob' as const,
            placeholder: cfg.placeholder,
            minAge: cfg.minAge ?? undefined,
            maxAge: cfg.maxAge ?? undefined,
          };
        }
        if (cfg.format === 'email') {
          return {
            ...baseQuestion,
            kind: 'short_text' as const,
            format: 'email' as const,
            placeholder: cfg.placeholder,
            disablePlusAddressing: cfg.disablePlusAddressing,
          };
        }
        if (cfg.format === 'country') {
          return {
            ...baseQuestion,
            kind: 'short_text' as const,
            format: 'country' as const,
            placeholder: cfg.placeholder,
            supportedCountries: cfg.supportedCountries ?? undefined,
          };
        }
        return {
          ...baseQuestion,
          kind: 'short_text' as const,
          format: cfg.format || 'any',
          placeholder: cfg.placeholder,
        };
      }

      if (section.config.kind === 'long_text') {
        return {
          ...baseQuestion,
          kind: 'long_text' as const,
          placeholder: section.config.placeholder,
        };
      }

      if (section.config.kind === 'number') {
        const { format, min, max, placeholder } = section.config;
        if (format?.formatKind === 'currency') {
          return {
            ...baseQuestion,
            kind: 'number',
            numberFormat: 'currency',
            currencyCode: format.code,
            min,
            max,
            placeholder,
          };
        }

        return {
          ...baseQuestion,
          kind: 'number',
          numberFormat: 'any',
          min,
          max,
          placeholder,
        };
      }

      if (section.config.kind === 'checkbox') {
        return {
          ...baseQuestion,
          kind: 'checkbox' as const,
        };
      }

      if (section.config.kind === 'dropdown') {
        return {
          ...baseQuestion,
          kind: 'dropdown' as const,
          options: section.config.options.map(o => ({ value: o, label: o })),
          includeOther: section.config.includeOther,
          placeholder: section.config.placeholder,
        };
      }

      if (section.config.kind === 'checkboxes') {
        return {
          ...baseQuestion,
          kind: 'checkboxes' as const,
          options: section.config.options.map(o => ({ value: o, label: o })),
        };
      }

      if (section.config.kind === 'multiple_choice') {
        return {
          ...baseQuestion,
          kind: 'multiple_choice' as const,
          options: section.config.options.map(o => ({ value: o, label: o })),
        };
      }

      if (section.config.kind === 'address') {
        return {
          ...baseQuestion,
          kind: 'address' as const,
          allowPoBox: section.config.allowPoBox,
          supportedCountries: section.config.supportedCountries ?? undefined,
        };
      }

      // Default fallback - treat unknown types as short_text
      return {
        ...baseQuestion,
        kind: 'short_text' as const,
        format: 'any' as const,
      };
    }),
  };
};

export default convertToFormData;
